import { toEngagementIdentity } from "@framework/api/honeypot/engagement/utils";

export type CommentContentSource = "wordpress" | "cyclone" | "in5";

const CANONICAL_TOKEN_PREFIX = /^(?:WP|CY|I5):/;

/** Builds a canonical Honeypot engagement token for cyclone comment counts. */
export function toCycloneCountToken(
  contentSource: CommentContentSource,
  id: string,
): string {
  const trimmed = id.trim();
  if (!trimmed) {
    return "";
  }

  if (CANONICAL_TOKEN_PREFIX.test(trimmed)) {
    return trimmed;
  }

  const prefix =
    contentSource === "cyclone" ? "CY" : contentSource === "in5" ? "I5" : "WP";
  return `${prefix}:${trimmed}`;
}

/**
 * Maps a comment-count store key to a Honeypot engagement token (WP:, CY:, or I5:).
 * Accepts pre-prefixed tokens and passes them through unchanged.
 */
export function toEngagementTokenForCountKey(
  countKey: string,
  contentSource?: CommentContentSource,
): string | null {
  const trimmed = countKey.trim();
  if (!trimmed) {
    return null;
  }

  if (CANONICAL_TOKEN_PREFIX.test(trimmed)) {
    return trimmed;
  }

  if (!contentSource) {
    return null;
  }

  return toCycloneCountToken(contentSource, trimmed);
}

/**
 * Normalizes a cyclone-comment-counts `c` query value to store key + Honeypot token.
 * Store keys use the same WP:/CY:/I5: prefixed form as engagement tokens.
 */
export function resolveCommentCountQueryEntry(param: string): {
  countKey: string;
  token: string;
} | null {
  const trimmed = param.trim();
  if (!trimmed) {
    return null;
  }

  const prefixedIdentity = toEngagementIdentity(trimmed);
  if (prefixedIdentity) {
    return {
      countKey: trimmed,
      token: prefixedIdentity.token,
    };
  }

  // In-5 tokens are client-side only until Honeypot adds engagement support.
  if (trimmed.startsWith("I5:")) {
    return {
      countKey: trimmed,
      token: trimmed,
    };
  }

  return null;
}
